import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import { 
  Calendar, Plus, RefreshCw, Users, Clock, AlertTriangle, 
  CheckCircle2, Sparkles, ChevronLeft, ChevronRight, Trash2, Brain, Loader2
} from 'lucide-react';
import { PageHeader, MetricCard, SectionHeader, ModernCard, modernTabsClass, modernTabTriggerClass } from '@/components/ModernUI';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TEAMS = ['Blue', 'Yellow', 'Green'];
const DISCIPLINES = ['RNCM', 'LPN', 'Wound RN', 'PRN'];
const PTO_TYPES = ['PTO', 'Sick', 'FMLA', 'Training', 'Other'];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export default function PTOCalendar() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [ptoEntries, setPtoEntries] = useState([]);
  const [calendarData, setCalendarData] = useState(null);
  const [clinicians, setClinicians] = useState([]);
  
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showAddClinicianDialog, setShowAddClinicianDialog] = useState(false);
  const [generatingCoverage, setGeneratingCoverage] = useState(null);
  const [selectedPTO, setSelectedPTO] = useState(null);
  const [activeCliniciansList, setActiveCliniciansList] = useState([]);
  
  const [newPTO, setNewPTO] = useState({
    clinician_name: '',
    team: '',
    discipline: 'RNCM',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    pto_type: 'PTO',
    full_day: true,
    notes: ''
  });

  const [newClinician, setNewClinician] = useState({
    name: '',
    team: '',
    discipline: 'RNCM',
    hire_date: ''
  });

  const fetchData = async () => {
    try {
      const [calendarRes, entriesRes, clinicianListRes, cliniciansRes] = await Promise.all([
        fetch(`${API_URL}/api/pto/calendar?month=${currentMonth}&year=${currentYear}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/pto`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/capacity/clinicians`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/clinicians/active`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (calendarRes.ok) {
        const data = await calendarRes.json();
        setCalendarData(data);
      }
      if (entriesRes.ok) {
        const entries = await entriesRes.json();
        setPtoEntries(entries);
      }
      
      // Get clinicians from new endpoint
      let allClinicians = [];
      if (clinicianListRes.ok) {
        const data = await clinicianListRes.json();
        allClinicians = (data.clinicians || []).map(c => c.name);
      }
      
      // Also get active clinicians
      if (cliniciansRes.ok) {
        const cliniciansList = await cliniciansRes.json();
        setActiveCliniciansList(cliniciansList);
        const activeNames = cliniciansList.map(c => c.name);
        allClinicians = [...new Set([...allClinicians, ...activeNames])];
      }
      
      // Sort alphabetically
      allClinicians.sort((a, b) => a.localeCompare(b));
      setClinicians(allClinicians);
      
    } catch (error) {
      toast.error('Failed to load PTO data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token, currentMonth, currentYear]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
    toast.success('Calendar refreshed');
  };

  const handleAddPTO = async () => {
    if (!newPTO.clinician_name || !newPTO.start_date || !newPTO.end_date) {
      toast.error('Please fill required fields');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/pto`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(newPTO)
      });

      if (response.ok) {
        toast.success('PTO entry added');
        setShowAddDialog(false);
        setNewPTO({
          clinician_name: '',
          team: '',
          discipline: 'RNCM',
          start_date: new Date().toISOString().split('T')[0],
          end_date: new Date().toISOString().split('T')[0],
          pto_type: 'PTO',
          full_day: true,
          notes: ''
        });
        fetchData();
      } else {
        const error = await response.json();
        toast.error('Failed to add PTO', { description: error.detail });
      }
    } catch (error) {
      toast.error('Error adding PTO');
    }
  };

  const handleAddClinician = async () => {
    if (!newClinician.name) {
      toast.error('Please enter clinician name');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/clinicians/active`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(newClinician)
      });

      if (response.ok) {
        toast.success('Clinician added');
        setShowAddClinicianDialog(false);
        setNewClinician({ name: '', team: '', discipline: 'RNCM', hire_date: '' });
        fetchData();
      } else {
        const error = await response.json();
        toast.error('Failed to add clinician', { description: error.detail });
      }
    } catch (error) {
      toast.error('Error adding clinician');
    }
  };

  const handleRemoveClinician = async (clinicianId) => {
    try {
      const response = await fetch(`${API_URL}/api/clinicians/active/${clinicianId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Clinician removed');
        fetchData();
      } else {
        toast.error('Failed to remove clinician');
      }
    } catch (error) {
      toast.error('Error removing clinician');
    }
  };

  const handleDeletePTO = async (ptoId) => {
    try {
      const response = await fetch(`${API_URL}/api/pto/${ptoId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('PTO entry deleted');
        fetchData();
      } else {
        toast.error('Failed to delete PTO');
      }
    } catch (error) {
      toast.error('Error deleting PTO');
    }
  };

  const handleGenerateCoverage = async (ptoId) => {
    setGeneratingCoverage(ptoId);
    try {
      const response = await fetch(`${API_URL}/api/pto/${ptoId}/coverage-plan`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const plan = await response.json();
        toast.success('Coverage plan generated');
        fetchData();
        setSelectedPTO({ ...ptoEntries.find(p => p.id === ptoId), coverage_plan: plan });
      } else {
        const error = await response.json();
        toast.error('Failed to generate plan', { description: error.detail });
      }
    } catch (error) {
      toast.error('Error generating coverage plan');
    } finally {
      setGeneratingCoverage(null);
    }
  };

  const prevMonth = () => {
    if (currentMonth === 1) {
      setCurrentMonth(12);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const nextMonth = () => {
    if (currentMonth === 12) {
      setCurrentMonth(1);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  // Generate calendar days
  const getDaysInMonth = (month, year) => new Date(year, month, 0).getDate();
  const getFirstDayOfMonth = (month, year) => new Date(year, month - 1, 1).getDay();
  
  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const days = [];
    
    // Empty cells for days before the first of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-24 bg-slate-50/50 dark:bg-slate-900/50" />);
    }
    
    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const ptoOnDay = (ptoEntries || []).filter(pto => {
        return pto && pto.start_date && pto.end_date && pto.start_date <= dateStr && pto.end_date >= dateStr;
      });
      
      const isToday = new Date().toISOString().split('T')[0] === dateStr;
      
      days.push(
        <div 
          key={day} 
          className={`h-24 p-1 border-t border-l border-slate-200 dark:border-slate-800 ${
            isToday ? 'bg-[#00b0ab]/10/50 dark:bg-[#00b0ab]/20' : 'bg-white dark:bg-slate-950'
          }`}
        >
          <div className={`text-sm font-medium mb-1 ${isToday ? 'text-[#00b0ab]' : ''}`}>
            {day}
          </div>
          <ScrollArea className="h-16">
            {ptoOnDay.map((pto, idx) => (
              <div
                key={idx}
                onClick={() => setSelectedPTO(pto)}
                className="text-[10px] px-1 py-0.5 mb-0.5 rounded truncate cursor-pointer hover:opacity-80 transition-opacity"
                style={{
                  backgroundColor: pto.pto_type === 'PTO' ? '#4F46E5' : 
                                   pto.pto_type === 'Sick' ? '#EF4444' :
                                   pto.pto_type === 'FMLA' ? '#F59E0B' : '#10B981',
                  color: 'white'
                }}
              >
                {(pto.clinician_name || 'Unknown').split(' ')[0]}
              </div>
            ))}
          </ScrollArea>
        </div>
      );
    }
    
    return days;
  };

  const upcomingPTO = (ptoEntries || [])
    .filter(pto => pto && pto.start_date && pto.start_date >= new Date().toISOString().split('T')[0])
    .slice(0, 10);

  const needsCoverage = (ptoEntries || []).filter(pto => 
    pto && !pto.coverage_plan && pto.start_date && pto.start_date >= new Date().toISOString().split('T')[0]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin h-8 w-8 border-4 border-[#00b0ab] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pto-calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold font-[Manrope] bg-gradient-to-r from-[#00b0ab] to-[#008a86] bg-clip-text text-transparent">
            PTO Calendar
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage time off and coverage planning
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing} data-testid="refresh-pto-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-gradient-to-r from-[#00b0ab] to-[#008a86]" data-testid="add-pto-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add PTO
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Schedule Time Off</DialogTitle>
                <DialogDescription>Add a new PTO entry to the calendar</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Clinician Name *</Label>
                  <Select value={newPTO.clinician_name} onValueChange={(v) => setNewPTO({...newPTO, clinician_name: v})}>
                    <SelectTrigger><SelectValue placeholder="Select clinician" /></SelectTrigger>
                    <SelectContent>
                      {clinicians.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Team</Label>
                    <Select value={newPTO.team} onValueChange={(v) => setNewPTO({...newPTO, team: v})}>
                      <SelectTrigger><SelectValue placeholder="Select team" /></SelectTrigger>
                      <SelectContent>
                        {TEAMS.map(t => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Discipline</Label>
                    <Select value={newPTO.discipline} onValueChange={(v) => setNewPTO({...newPTO, discipline: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {DISCIPLINES.map(d => (
                          <SelectItem key={d} value={d}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date *</Label>
                    <Input 
                      type="date" 
                      value={newPTO.start_date} 
                      onChange={(e) => setNewPTO({...newPTO, start_date: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date *</Label>
                    <Input 
                      type="date" 
                      value={newPTO.end_date} 
                      onChange={(e) => setNewPTO({...newPTO, end_date: e.target.value})}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={newPTO.pto_type} onValueChange={(v) => setNewPTO({...newPTO, pto_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PTO_TYPES.map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea 
                    value={newPTO.notes} 
                    onChange={(e) => setNewPTO({...newPTO, notes: e.target.value})}
                    placeholder="Additional notes..."
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                <Button onClick={handleAddPTO} data-testid="confirm-add-pto">Add PTO</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-indigo-500/10 to-violet-500/10 border-[#00b0ab]/30 dark:border-indigo-800">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-[#00b0ab]/100/20 flex items-center justify-center">
              <Calendar className="h-6 w-6 text-[#00b0ab]" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total PTO This Month</p>
              <p className="text-2xl font-bold">{calendarData?.pto_entries?.length || 0}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-200 dark:border-amber-800">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <AlertTriangle className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Needs Coverage Plan</p>
              <p className="text-2xl font-bold">{needsCoverage.length}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 border-emerald-200 dark:border-emerald-800">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <Users className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Active Clinicians</p>
              <p className="text-2xl font-bold">{calendarData?.clinicians?.length || 0}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="calendar" className="space-y-4">
        <TabsList className="grid w-full sm:w-auto sm:inline-grid grid-cols-4 h-auto">
          <TabsTrigger value="calendar" className="gap-2">
            <Calendar className="h-4 w-4" /> Calendar
          </TabsTrigger>
          <TabsTrigger value="list" className="gap-2">
            <Clock className="h-4 w-4" /> Upcoming
          </TabsTrigger>
          <TabsTrigger value="coverage" className="gap-2">
            <Brain className="h-4 w-4" /> Coverage
          </TabsTrigger>
          <TabsTrigger value="clinicians" className="gap-2">
            <Users className="h-4 w-4" /> Clinicians
          </TabsTrigger>
        </TabsList>

        {/* Calendar View */}
        <TabsContent value="calendar">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" onClick={prevMonth}>
                    <ChevronLeft className="h-5 w-5" />
                  </Button>
                  <span className="min-w-[200px] text-center">
                    {MONTHS[currentMonth - 1]} {currentYear}
                  </span>
                  <Button variant="ghost" size="icon" onClick={nextMonth}>
                    <ChevronRight className="h-5 w-5" />
                  </Button>
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {/* Day headers */}
              <div className="grid grid-cols-7 mb-1">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
                    {day}
                  </div>
                ))}
              </div>
              {/* Calendar grid */}
              <div className="grid grid-cols-7 border-r border-b border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden">
                {renderCalendar()}
              </div>
              {/* Legend */}
              <div className="flex flex-wrap gap-4 mt-4 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-[#4F46E5]" /> PTO
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-[#EF4444]" /> Sick
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-[#F59E0B]" /> FMLA
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-[#10B981]" /> Other
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Upcoming List */}
        <TabsContent value="list">
          <Card>
            <CardHeader>
              <CardTitle>Upcoming Time Off</CardTitle>
              <CardDescription>All scheduled PTO entries</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Clinician</TableHead>
                    <TableHead>Team</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Start</TableHead>
                    <TableHead>End</TableHead>
                    <TableHead>Coverage</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {upcomingPTO.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                        No upcoming PTO scheduled
                      </TableCell>
                    </TableRow>
                  ) : (
                    upcomingPTO.map((pto) => (
                      <TableRow key={pto.id}>
                        <TableCell className="font-medium">{pto.clinician_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{pto.team || '-'}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            pto.pto_type === 'Sick' ? 'bg-red-500' :
                            pto.pto_type === 'FMLA' ? 'bg-amber-500' :
                            pto.pto_type === 'Training' ? 'bg-blue-500' : 'bg-[#00b0ab]/100'
                          }>
                            {pto.pto_type}
                          </Badge>
                        </TableCell>
                        <TableCell>{pto.start_date}</TableCell>
                        <TableCell>{pto.end_date}</TableCell>
                        <TableCell>
                          {pto.coverage_plan ? (
                            <Badge className="bg-emerald-500">
                              <CheckCircle2 className="h-3 w-3 mr-1" /> Planned
                            </Badge>
                          ) : (
                            <Badge variant="destructive">
                              <AlertTriangle className="h-3 w-3 mr-1" /> Needed
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {!pto.coverage_plan && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleGenerateCoverage(pto.id)}
                                disabled={generatingCoverage === pto.id}
                              >
                                {generatingCoverage === pto.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Sparkles className="h-4 w-4" />
                                )}
                              </Button>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => setSelectedPTO(pto)}
                            >
                              View
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-500 hover:text-red-600"
                              onClick={() => handleDeletePTO(pto.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Coverage Plans */}
        <TabsContent value="coverage">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="h-5 w-5 text-[#00b0ab]" />
                AI Coverage Planning
              </CardTitle>
              <CardDescription>
                AI-generated staffing recommendations when clinicians are out
              </CardDescription>
            </CardHeader>
            <CardContent>
              {needsCoverage.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-emerald-500" />
                  <p>All PTO entries have coverage plans!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {needsCoverage.map((pto) => (
                    <div 
                      key={pto.id} 
                      className="p-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/20"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{pto.clinician_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {pto.team} Team • {pto.start_date} to {pto.end_date}
                          </p>
                        </div>
                        <Button 
                          size="sm"
                          onClick={() => handleGenerateCoverage(pto.id)}
                          disabled={generatingCoverage === pto.id}
                          className="bg-gradient-to-r from-[#00b0ab] to-[#008a86]"
                        >
                          {generatingCoverage === pto.id ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <Sparkles className="h-4 w-4 mr-2" />
                              Generate Plan
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Active Clinicians Tab */}
        <TabsContent value="clinicians">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-[#00b0ab]" />
                    Active Clinicians
                  </CardTitle>
                  <CardDescription>
                    Manage active clinicians for PTO scheduling
                  </CardDescription>
                </div>
                <Button 
                  size="sm" 
                  onClick={() => setShowAddClinicianDialog(true)}
                  className="bg-gradient-to-r from-[#00b0ab] to-[#008a86]"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Clinician
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Team</TableHead>
                    <TableHead>Discipline</TableHead>
                    <TableHead>Hire Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activeCliniciansList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                        No active clinicians. Add clinicians to enable PTO scheduling.
                      </TableCell>
                    </TableRow>
                  ) : (
                    activeCliniciansList.map((clinician) => (
                      <TableRow key={clinician.id}>
                        <TableCell className="font-medium">{clinician.name}</TableCell>
                        <TableCell>
                          <Badge 
                            className={
                              clinician.team === 'Blue' ? 'bg-blue-500 text-white' :
                              clinician.team === 'Yellow' ? 'bg-yellow-400 text-black' :
                              clinician.team === 'Green' ? 'bg-green-500 text-white' :
                              'bg-gray-200 text-gray-800'
                            }
                          >
                            {clinician.team || 'N/A'}
                          </Badge>
                        </TableCell>
                        <TableCell>{clinician.discipline || 'RNCM'}</TableCell>
                        <TableCell>{clinician.hire_date || '-'}</TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-500 hover:text-red-600"
                            onClick={() => handleRemoveClinician(clinician.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Clinician Dialog */}
      <Dialog open={showAddClinicianDialog} onOpenChange={setShowAddClinicianDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Active Clinician</DialogTitle>
            <DialogDescription>Add a new clinician to the active roster</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Clinician Name *</Label>
              <Input 
                value={newClinician.name} 
                onChange={(e) => setNewClinician({...newClinician, name: e.target.value})}
                placeholder="Enter clinician name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Team</Label>
                <Select value={newClinician.team} onValueChange={(v) => setNewClinician({...newClinician, team: v})}>
                  <SelectTrigger><SelectValue placeholder="Select team" /></SelectTrigger>
                  <SelectContent>
                    {TEAMS.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Discipline</Label>
                <Select value={newClinician.discipline} onValueChange={(v) => setNewClinician({...newClinician, discipline: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DISCIPLINES.map(d => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Hire Date</Label>
              <Input 
                type="date"
                value={newClinician.hire_date} 
                onChange={(e) => setNewClinician({...newClinician, hire_date: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddClinicianDialog(false)}>Cancel</Button>
            <Button onClick={handleAddClinician}>Add Clinician</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PTO Detail Dialog */}
      <Dialog open={!!selectedPTO} onOpenChange={() => setSelectedPTO(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-[#00b0ab]" />
              PTO Details: {selectedPTO?.clinician_name}
            </DialogTitle>
          </DialogHeader>
          {selectedPTO && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Team</p>
                  <p className="font-medium">{selectedPTO.team || 'Not assigned'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Discipline</p>
                  <p className="font-medium">{selectedPTO.discipline || 'RNCM'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Type</p>
                  <Badge className={
                    selectedPTO.pto_type === 'Sick' ? 'bg-red-500' :
                    selectedPTO.pto_type === 'FMLA' ? 'bg-amber-500' : 'bg-[#00b0ab]/100'
                  }>
                    {selectedPTO.pto_type}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Duration</p>
                  <p className="font-medium">{selectedPTO.start_date} → {selectedPTO.end_date}</p>
                </div>
              </div>
              
              {selectedPTO.notes && (
                <div>
                  <p className="text-sm text-muted-foreground">Notes</p>
                  <p className="mt-1">{selectedPTO.notes}</p>
                </div>
              )}

              {selectedPTO.coverage_plan ? (
                <div className="mt-4 p-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                    <p className="font-medium">Coverage Plan Available</p>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">
                    Visits to cover: {selectedPTO.coverage_plan.total_visits_to_cover || 'N/A'}
                  </p>
                  <ScrollArea className="h-[200px] rounded-lg border p-3 bg-slate-100 dark:bg-slate-800">
                    <div className="prose prose-sm dark:prose-invert max-w-none">
                      <ReactMarkdown
                        components={{
                          h1: ({children}) => <h1 className="text-lg font-bold mt-3 mb-1 text-emerald-600 dark:text-emerald-400">{children}</h1>,
                          h2: ({children}) => <h2 className="text-base font-bold mt-3 mb-1 text-emerald-600 dark:text-emerald-400">{children}</h2>,
                          h3: ({children}) => <h3 className="text-sm font-semibold mt-2 mb-1 text-emerald-500 dark:text-emerald-400">{children}</h3>,
                          p: ({children}) => <p className="my-1 text-sm leading-relaxed">{children}</p>,
                          ul: ({children}) => <ul className="list-disc ml-4 my-1 space-y-0.5">{children}</ul>,
                          ol: ({children}) => <ol className="list-decimal ml-4 my-1 space-y-0.5">{children}</ol>,
                          li: ({children}) => <li className="text-sm leading-relaxed">{children}</li>,
                          strong: ({children}) => <strong className="font-semibold">{children}</strong>,
                        }}
                      >
                        {selectedPTO.coverage_plan.ai_recommendation}
                      </ReactMarkdown>
                    </div>
                  </ScrollArea>
                </div>
              ) : (
                <div className="mt-4 p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-amber-500" />
                      <p className="font-medium">No Coverage Plan</p>
                    </div>
                    <Button 
                      size="sm"
                      onClick={() => handleGenerateCoverage(selectedPTO.id)}
                      disabled={generatingCoverage === selectedPTO.id}
                      className="bg-gradient-to-r from-[#00b0ab] to-[#008a86]"
                    >
                      {generatingCoverage === selectedPTO.id ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Sparkles className="h-4 w-4 mr-2" />
                      )}
                      Generate with AI
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
